import { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import mammoth from "mammoth";
import { Loader2, FileWarning } from "lucide-react";
import API from "../lib/api";
import { getRenderType, getFileExtension, parseCsvRows, MAX_TEXT_CHARS } from "../lib/filePreview";

const MAX_XLSX_ROWS = 50000;
const MAX_XLSX_COLS = 300;
const MAX_PARSE_BYTES = 100 * 1024 * 1024;

function formatSize(size) {
  const bytes = Number(size || 0);
  if (!bytes) return "";
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

/** Split text into segments, marking parts that match the search term. */
function highlightSegments(text, search) {
  const term = String(search || "").trim();
  if (!term) return [{ text, match: false }];
  const segments = [];
  const lower = String(text || "").toLowerCase();
  const needle = term.toLowerCase();
  let i = 0;
  while (i < lower.length) {
    const idx = lower.indexOf(needle, i);
    if (idx === -1) {
      segments.push({ text: String(text).slice(i), match: false });
      break;
    }
    if (idx > i) segments.push({ text: String(text).slice(i, idx), match: false });
    segments.push({ text: String(text).slice(idx, idx + needle.length), match: true });
    i = idx + needle.length;
  }
  return segments.length ? segments : [{ text, match: false }];
}

function HighlightedText({ text, search }) {
  const segments = useMemo(() => highlightSegments(text, search), [text, search]);
  if (segments.length === 1 && !segments[0].match) return text;
  return segments.map((seg, i) =>
    seg.match ? (
      <mark key={i} className="rounded-sm bg-amber-200 text-amber-950 dark:bg-amber-400/30 dark:text-amber-200">
        {seg.text}
      </mark>
    ) : (
      <span key={i}>{seg.text}</span>
    )
  );
}

function buildOpenInTabUrl(renderType, text, workbook) {
  try {
    if (renderType === "spreadsheet" && workbook) {
      const sheet = workbook.Sheets[workbook.SheetNames?.[0]];
      if (!sheet) return null;
      const csv = XLSX.utils.sheet_to_csv(sheet);
      return URL.createObjectURL(new Blob([csv], { type: "text/plain" }));
    }
    if (text) {
      return URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    }
  } catch (e) {
    console.warn("Failed to build open-in-tab blob URL", e);
  }
  return null;
}

function PreviewTable({ rows, search, emptyMessage }) {
  if (!rows || rows.length === 0) {
    return <p className="p-6 text-center text-xs font-medium text-slate-400">{emptyMessage || "No rows found."}</p>;
  }
  const maxCols = Math.max(...rows.map((r) => r.length), 0);
  return (
    <div className="h-full w-full overflow-auto p-3">
      <table className="w-full text-left text-xs border-collapse">
        <thead>
          <tr className="sticky top-0 z-10 bg-slate-100 text-[11px] uppercase tracking-wider text-slate-500 dark:bg-slate-800 dark:text-slate-400">
            {Array.from({ length: maxCols }).map((_, i) => (
              <th key={i} className="whitespace-nowrap border-b border-slate-200 px-3 py-2 font-bold dark:border-slate-700">
                <HighlightedText text={rows[0]?.[i] != null ? String(rows[0][i]).slice(0, 60) : ""} search={search} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.slice(1).map((row, ri) => (
            <tr key={ri} className="hover:bg-indigo-500/5">
              {Array.from({ length: maxCols }).map((_, ci) => (
                <td key={ci} className="whitespace-nowrap border-b border-slate-100 px-3 py-1.5 font-mono text-[11px] text-slate-600 dark:border-white/[0.06] dark:text-slate-300">
                  <HighlightedText text={row[ci] != null ? String(row[ci]).slice(0, 120) : ""} search={search} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TextView({ text, search, filename }) {
  const isEmail = ["eml", "log"].includes(getFileExtension(filename));
  const content = String(text || "");
  if (!content.trim()) {
    return <p className="p-6 text-center text-xs font-medium text-slate-400">Empty file.</p>;
  }
  return (
    <div className="h-full w-full overflow-auto p-4">
      <pre className={`whitespace-pre-wrap break-words font-mono text-xs leading-relaxed text-slate-700 dark:text-slate-300 ${isEmail ? "" : "font-sans"}`}>
        <HighlightedText text={content} search={search} />
      </pre>
    </div>
  );
}

export default function EvidencePdfFrame({ auditRunId, item, page, search, onUrl, className = "" }) {
  const [status, setStatus] = useState("loading");
  const [url, setUrl] = useState(null);
  const [textContent, setTextContent] = useState("");
  const [workbook, setWorkbook] = useState(null);
  const [docxHtml, setDocxHtml] = useState("");
  const [fileSize, setFileSize] = useState(null);
  const blobUrlRef = useRef(null);

  const renderType = useMemo(() => getRenderType(item?.filename), [item?.filename]);

  useEffect(() => {
    if (!item?.id || !auditRunId) return;
    let cancelled = false;
    setStatus("loading");
    setUrl(null);
    setTextContent("");
    setWorkbook(null);
    setDocxHtml("");
    setFileSize(null);

    const load = async () => {
      try {
        const { data } = await API.get(
          `/audit-runs/${auditRunId}/documents/${item.id}/download`
        );
        if (cancelled) return;
        const base = String(data.download_url || "");
        setFileSize(data.file_size);

        if (renderType === "pdf" || renderType === "image" || renderType === "unsupported") {
          const parts = [];
          if (page) parts.push(`page=${encodeURIComponent(page)}`);
          if (search) parts.push(`search=${encodeURIComponent(String(search).slice(0, 120))}`);
          setUrl(parts.length ? `${base}#${parts.join("&")}` : base);
          setStatus("ready");
          onUrl?.(base);
          return;
        }

        if ((data.file_size || 0) > MAX_PARSE_BYTES) {
          setUrl(base);
          setStatus("too_large");
          onUrl?.(base);
          return;
        }

        let contentText = "";
        let wb = null;
        let html = "";

        try {
          if (renderType === "spreadsheet") {
            const res = await API.get(
              `/audit-runs/${auditRunId}/documents/${item.id}/preview`,
              { responseType: "arraybuffer" }
            );
            wb = XLSX.read(res.data, { type: "array" });
          } else if (renderType === "docx") {
            const res = await API.get(
              `/audit-runs/${auditRunId}/documents/${item.id}/preview`,
              { responseType: "arraybuffer" }
            );
            const result = await mammoth.convertToHtml({ arrayBuffer: res.data });
            html = result.value || "<p>Empty document.</p>";
          } else {
            const res = await API.get(
              `/audit-runs/${auditRunId}/documents/${item.id}/preview`,
              { responseType: "text" }
            );
            contentText = String(res.data || "");
          }
        } catch (previewErr) {
          console.warn("Preview endpoint failed; falling back to presigned URL", previewErr);
          const res = await fetch(base);
          if (!res.ok) throw new Error(`Fetch failed (${res.status})`);
          if (renderType === "spreadsheet") {
            wb = XLSX.read(await res.arrayBuffer(), { type: "array" });
          } else if (renderType === "docx") {
            const buffer = await res.arrayBuffer();
            const result = await mammoth.convertToHtml({ arrayBuffer: buffer });
            html = result.value || "<p>Empty document.</p>";
          } else {
            contentText = await res.text();
          }
        }

        if (cancelled) return;

        if (renderType === "json") {
          try {
            contentText = JSON.stringify(JSON.parse(contentText || "{}"), null, 2);
          } catch {
            // Keep raw text if parse fails
          }
        }

        contentText = contentText.slice(0, MAX_TEXT_CHARS);
        setTextContent(contentText);
        setWorkbook(wb);
        setDocxHtml(html);

        const tabUrl = buildOpenInTabUrl(renderType, contentText, wb);
        if (tabUrl) {
          if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
          blobUrlRef.current = tabUrl;
        }
        setUrl(base);
        setStatus("ready");
        onUrl?.(tabUrl || base);
      } catch (e) {
        console.error("Failed to load evidence document", e);
        if (!cancelled) setStatus("error");
      }
    };

    load();
    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [auditRunId, item?.id, page, search, renderType]);

  if (status === "loading") {
    return (
      <div className={`flex h-full w-full items-center justify-center ${className}`.trim()}>
        <div className="flex flex-col items-center gap-3 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin" />
          <p className="text-xs font-medium">Loading document...</p>
        </div>
      </div>
    );
  }

  if (status === "error" || !url) {
    return (
      <div className={`flex h-full w-full items-center justify-center ${className}`.trim()}>
        <div className="flex flex-col items-center gap-3 text-center text-slate-400">
          <FileWarning className="h-8 w-8" />
          <p className="max-w-xs text-xs font-medium">
            Could not load this document for inline preview. Use the open action in the
            document header to view it in a new tab.
          </p>
        </div>
      </div>
    );
  }

  if (status === "too_large") {
    const size = formatSize(fileSize);
    return (
      <div className={`flex h-full w-full items-center justify-center ${className}`.trim()}>
        <div className="flex flex-col items-center gap-3 text-center text-slate-400">
          <FileWarning className="h-8 w-8" />
          <p className="max-w-xs text-xs font-medium">
            This document is too large to preview inline{size ? ` (${size})` : ""}. Use the open
            action in the document header to view it in a new tab.
          </p>
        </div>
      </div>
    );
  }

  if (renderType === "pdf") {
    return (
      <iframe
        src={url}
        title={item?.filename || "Evidence document"}
        className={`h-full w-full border-0 bg-white ${className}`.trim()}
      />
    );
  }

  if (renderType === "image") {
    return (
      <div className={`flex h-full w-full items-center justify-center p-4 bg-slate-900/5 dark:bg-black/40 ${className}`.trim()}>
        <img
          src={url}
          alt={item?.filename || "Evidence image"}
          className="max-h-full max-w-full rounded-xl object-contain shadow-sm"
        />
      </div>
    );
  }

  if (renderType === "csv") {
    const rows = parseCsvRows(textContent);
    return (
      <div className={`h-full w-full overflow-hidden bg-white dark:bg-slate-900 ${className}`.trim()}>
        <PreviewTable rows={rows} search={search} />
      </div>
    );
  }

  if (renderType === "spreadsheet") {
    return <SpreadsheetPreview workbook={workbook} search={search} filename={item?.filename} />;
  }

  if (renderType === "docx") {
    return (
      <div className={`h-full w-full overflow-auto bg-white p-6 dark:bg-slate-900 ${className}`.trim()}>
        <div
          className="prose prose-slate prose-sm max-w-none dark:prose-invert"
          dangerouslySetInnerHTML={{ __html: docxHtml }}
        />
      </div>
    );
  }

  if (renderType === "text" || renderType === "json") {
    return (
      <div className={`h-full w-full overflow-hidden bg-white dark:bg-slate-900 ${className}`.trim()}>
        <TextView text={textContent} search={search} filename={item?.filename} />
      </div>
    );
  }

  return (
    <iframe
      src={url}
      title={item?.filename || "Evidence document"}
      className={`h-full w-full border-0 bg-white ${className}`.trim()}
    />
  );
}

function SpreadsheetPreview({ workbook, search, filename }) {
  const [sheetIndex, setSheetIndex] = useState(0);

  const sheetNames = workbook?.SheetNames || [];

  useEffect(() => {
    if (sheetIndex >= sheetNames.length) setSheetIndex(0);
  }, [sheetIndex, sheetNames.length]);

  const rows = useMemo(() => {
    const sheetName = sheetNames[sheetIndex];
    if (!sheetName) return [];
    const ws = workbook.Sheets[sheetName];
    const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
    const maxRows = Math.min(raw.length, MAX_XLSX_ROWS);
    const maxCols = Math.min(
      Math.max(...raw.slice(0, maxRows).map((r) => r.length), 0),
      MAX_XLSX_COLS
    );
    return raw
      .slice(0, maxRows)
      .map((r) => Array.from({ length: maxCols }, (_, i) => String(r[i] ?? "").slice(0, 120)));
  }, [workbook, sheetIndex, sheetNames]);

  if (sheetNames.length === 0) {
    return (
      <div className={`flex h-full w-full items-center justify-center bg-white dark:bg-slate-900 ${className || ""}`.trim()}>
        <p className="text-xs font-medium text-slate-400">No sheets found in this workbook.</p>
      </div>
    );
  }

  return (
    <div className={`flex h-full w-full flex-col bg-white dark:bg-slate-900 ${className || ""}`.trim()}>
      {sheetNames.length > 1 && (
        <div className="flex shrink-0 items-center gap-1 overflow-x-auto border-b border-slate-200 px-2 py-1.5 dark:border-white/[0.08]">
          {sheetNames.map((name, i) => (
            <button
              key={name}
              type="button"
              onClick={() => setSheetIndex(i)}
              className={`whitespace-nowrap rounded-md px-2.5 py-1 text-[10px] font-bold transition-colors ${
                i === sheetIndex
                  ? "bg-indigo-600 text-white"
                  : "text-slate-500 hover:bg-slate-100 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-white/[0.06] dark:hover:text-slate-200"
              }`}
            >
              {name}
            </button>
          ))}
        </div>
      )}
      <div className="min-h-0 flex-1">
        <PreviewTable rows={rows} search={search} emptyMessage="No data found in this sheet." />
      </div>
    </div>
  );
}
